import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { CommonIcons, DEFAULT_EMBEDD_SETTINGS, WebChatBannerSettings } from "@42maru/webchat";
import { INVALID_CODES } from '@/core/validation';
import {
    DEFAULT_BUTTON_ICON,
    BUTTON_TEXT_MAX_LENGTH,
    refineBannerSettings,
    isValidBannerText
} from '@/util/helper/webchatThemeHelper';
import { useUploadedFiles } from '@/hook';
import { ImageUploadBtn, TextInputGroup } from '@/component/input';
import ClickableImg from '@/component/ClickableImg';
import { DeleteIcon } from '@/assets/svgs/icons';

import { WebchatEmbeddedFormProps } from ".";
import { WebchatRadioGroup, WebchatRadioGroupOption, WebchatColorPicker } from '../component';
import { default as ButtonPositionInputGroup } from './BannerButtonPositionInputGroup';


const BUTTON_COLORS = [ "#FC554F", "#FDA735", "#32BEBE", "#3787E8", "#A04BB6", "#441A98", "#878682", "#2A2A37" ];

const BUTTON_SHAPE_OPTIONS: WebchatRadioGroupOption<"round" | "square">[] = [
    { value: "round", labelKey: "i09106" },
    { value: "square", labelKey: "i09107" }
];

const BannerButtonInputGroup: React.FC<WebchatEmbeddedFormProps> = (props) => {
    const { embeddedSettings, legend, onChange: propsOnChange } = props;

    const intl = useIntl();
    const { add, remove } = useUploadedFiles();

    const textValidation = useMemo(() => {
        const validation = isValidBannerText(embeddedSettings.bannerText);
        let errorText = undefined;
        if (validation.code === INVALID_CODES.MAX) {
            errorText = intl.formatMessage({ id: "i09049" }, { maxLength: BUTTON_TEXT_MAX_LENGTH });
        }
        return {
            isValid: validation.isValid,
            errorText
        };
    }, [embeddedSettings.bannerText, intl]);

    const onChange = (changes: WebChatBannerSettings) => {
        propsOnChange(refineBannerSettings(changes));
    };

    const updateBannerSetting = (changes: {[key: string]: any}) => {
        onChange({
            ...embeddedSettings,
            ...changes,
            mobile: {
                ...embeddedSettings.mobile!,
                ...changes,
            }
        });
    };

    const onChangeColor = (color: string) => {
        updateBannerSetting({
            bannerBackgroundColorStart: color,
            bannerBackgroundColorEnd: color,
            buttonBackgroundColor: color,
            buttonBackgroundColorStart: color,
            buttonBackgroundColorEnd: color,
        });
    };

    const onUploadIcon = (file: File, url: string) => {
        if (embeddedSettings.buttonIcon) {
            remove(embeddedSettings.buttonIcon);
        }
        add(file, url);
        updateBannerSetting({ buttonIcon: url });
    };

    const onDeleteIcon = () => {
        const url = embeddedSettings.buttonIcon;
        if (url) {
            remove(url);
        }
        updateBannerSetting({ buttonIcon: DEFAULT_BUTTON_ICON });
    };

    return (
        <fieldset className="webchat-setting-card-grid gap-y-6">
            <legend className="sr-only">
                {legend}
            </legend>
            <div className="field-label">
                {intl.formatMessage({ id: "i09068" })}
            </div>
            <div className="h-input-default flex items-center gap-4">
                {(embeddedSettings.buttonIcon && embeddedSettings.buttonIcon !== DEFAULT_EMBEDD_SETTINGS.buttonIcon) ? (<>
                    <ClickableImg src={embeddedSettings.buttonIcon}
                                  alt={intl.formatMessage({ id: "i09068" })}
                                  height={24}
                                  width={24}
                    />
                    <button aria-label={intl.formatMessage({ id: "b0006" })}
                            onClick={onDeleteIcon}
                    >
                        <DeleteIcon className="text-point-red size-4" />
                    </button>
                </>) : (<>
                    <div className="size-[36px] flex items-center justify-center rounded-full overflow-hidden"
                         style={{ background: embeddedSettings.buttonBackgroundColor }}
                    >
                        {CommonIcons.defaultBannerBotLogo(24)}
                    </div>
                    <ImageUploadBtn onUpload={onUploadIcon} />
                </>)}
            </div>

            <div id="webchat-button-shape"
                 className="field-label">
                {intl.formatMessage({ id: "i09069" })}
            </div>
            <WebchatRadioGroup name="webchat-button-shape"
                               aria-labelledby="webchat-button-shape"
                               options={BUTTON_SHAPE_OPTIONS}
                               selectedValue={embeddedSettings.buttonIconImageCircle ? "round" : "square"}
                               onChange={value => updateBannerSetting({ buttonIconImageCircle: value === "round" })}
            />

            <div className="label-input-grid-label field-label">
                {intl.formatMessage({ id: "i09070" })}
            </div>
            <div className="flex flex-col gap-2.5">
                <div className="flex gap-2"
                     role="group"
                     aria-label={intl.formatMessage({ id: "i09070" })}
                >
                    {BUTTON_COLORS.map(color => (
                        <div key={color}
                             className={classNames(
                                 "flex justify-center items-center p-1.5 border border-transparent rounded-full",
                                 embeddedSettings.bannerBackgroundColorStart?.toUpperCase() === color.toUpperCase()
                                     && "!border-black-300"
                             )}
                        >
                            <button className="color-circle"
                                    aria-label={color}
                                    style={{ background: color }}
                                    onClick={() => onChangeColor(color)}
                            />
                        </div>
                    ))}
                </div>
                <WebchatColorPicker aria-label={intl.formatMessage({ id: "i09070" })}
                                    colorKey="bannerBackgroundColorStart"
                                    colors={embeddedSettings}
                                    onChange={onChangeColor}
                />
            </div>

            <div className="col-span-2 flex flex-col gap-1.5">
                <label htmlFor="webchat-setting-banner-text" className="field-label">
                    {intl.formatMessage({ id: "i09071" })}
                </label>
                <TextInputGroup id="webchat-setting-banner-text"
                                value={embeddedSettings.bannerText ?? ""}
                                placeholder={intl.formatMessage({ id: "i09006" })}
                                invalid={!textValidation.isValid}
                                invalidText={textValidation.errorText}
                                onChange={value => updateBannerSetting({ bannerText: value })}
                />
            </div>

            <div className="my-2 col-span-2 flex flex-col gap-2">
                <div className="field-label">
                    {intl.formatMessage({ id: "i09072" })}
                </div>
                <ButtonPositionInputGroup embeddedSettings={embeddedSettings}
                                          legend={intl.formatMessage({ id: "i09072" })}
                                          onChange={onChange}
                />
            </div>
        </fieldset>
    );
};


export default BannerButtonInputGroup;
